// 역할: 사이드바 영역 담당
import { useRef, useEffect } from 'react';

import { SWIPE } from '../../constants/layout';
import { cn } from '../../utils/cn';
import { SidebarMenu } from '../Sidebar/SidebarMenu';

interface SidebarAreaProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose?: () => void;
  isMobile?: boolean;
}

export const SidebarArea = ({ isOpen, onToggle, onClose, isMobile = false }: SidebarAreaProps) => {
  const sidebarRef = useRef<HTMLElement>(null);
  const startXRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  // 터치 스와이프로 메뉴 닫기
  useEffect(() => {
    if (!isMobile || !isOpen || !onClose) return;

    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleTouchStart = (e: TouchEvent) => {
      const firstTouch = e.touches[0];
      if (!firstTouch) return;
      startXRef.current = firstTouch.clientX;
      currentXRef.current = firstTouch.clientX;
      isDraggingRef.current = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDraggingRef.current) return;

      currentXRef.current = e.touches[0]?.clientX ?? currentXRef.current;
      const diff = currentXRef.current - startXRef.current;

      // 왼쪽으로 스와이프할 때만 반응
      if (diff < 0) {
        sidebar.style.transform = `translateX(${diff}px)`;
      }
    };

    const handleTouchEnd = () => {
      if (!isDraggingRef.current) return;

      const diff = currentXRef.current - startXRef.current;

      // ✅ P2-2: Use constant instead of magic number
      if (diff < -SWIPE.CLOSE_THRESHOLD) {
        onClose();
      }

      // 원래 위치로 복원
      sidebar.style.transform = '';
      isDraggingRef.current = false;
    };

    // ✅ P0-2: Add passive listeners to improve scroll performance
    sidebar.addEventListener('touchstart', handleTouchStart, { passive: true });
    sidebar.addEventListener('touchmove', handleTouchMove, { passive: true });
    sidebar.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      sidebar.removeEventListener('touchstart', handleTouchStart);
      sidebar.removeEventListener('touchmove', handleTouchMove);
      sidebar.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, isOpen, onClose]);

  if (isMobile) {
    return (
      <dialog
        ref={sidebarRef as React.RefObject<HTMLDialogElement | null>}
        open={isOpen}
        className={cn(
          'fixed top-0 left-0 h-full w-[83%] bg-white z-50 m-0 p-0 border-none max-h-none max-w-none',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label='Main navigation'
        aria-modal='true'
      >
        <div className='h-full flex flex-col'>
          {/* 사이드바 메뉴 */}
          <div className='flex-1 overflow-hidden'>
            <SidebarMenu isOpen={true} onToggle={onToggle} />
          </div>
        </div>
      </dialog>
    );
  }

  return (
    <div className={`${isOpen ? 'w-[400px]' : 'w-0'} transition-all duration-300 overflow-hidden`}>
      <div className='h-full flex flex-col bg-white'>
        {/* 사이드바 메뉴 */}
        <div className='flex-1 overflow-hidden'>
          <SidebarMenu isOpen={isOpen} onToggle={onToggle} />
        </div>
      </div>
    </div>
  );
};
